import React, { useState, useEffect, useRef } from "react";
import theme from "../../data/theme";
import { POS_COLORS } from "../../data/players";
import { nominationSecondsLeft, boardIsComplete, teamIsFull } from "../../utils/liveBoard";
import { boardFocus, turnSeat } from "../../utils/boardFocus";
import { clipFor } from "../../utils/playerClips";

/**
 * NominationBlock — the single loud element, in one of two states.
 *
 *   WAITING   sale committed  -> "RACHEL'S NOMINATION", clock counting down
 *   ON BLOCK  nomination made -> the player, big, clock stopped
 *
 * Both are facts, so both are on /watch and the shared screen always says
 * something. The empty state used to read "Nobody on the block", which told a
 * room that had just watched a sale absolutely nothing.
 *
 * 🔴 THIS IS A BOARD COMPONENT. Name, NFL team, photo. No rank, no ESTIMATE,
 * nothing this app thinks a player is worth — that is ASSISTANT information
 * (see the header of WatchPage.jsx). There is no price PROP and no posRank;
 * both were removed rather than hidden, so there is nothing to accidentally
 * pass, and nothing here imports dynamicPrices.
 *
 * ⚠️ THE ONE NUMBER THAT IS ALLOWED is the price a player ACTUALLY SOLD FOR,
 * in the SOLD state below, read off `board.sales` — never off a prop. Rule 1 in
 * the WatchPage header names that case exactly: a sale price is a fact, and it
 * is already on the roster cards. An estimate and a result are not the same
 * number, and the structural test in watchPage.test.js distinguishes them by
 * where the value comes from rather than by the word "price".
 *
 * REUSES THE EXISTING GLOW so this reads as the same product as the mock
 * draft's auction card: `.mock-nominating-panel` (auction.css:136) is the
 * centred panel and the pulse is `nomGlowPulse` (auction.css:52), taken by name
 * at an infinite count because the player SITS here for the whole bidding and
 * the shipped `.nom-glow-pulse` class stops after three pulses.
 *
 * WHOSE TURN IT IS is answered twice on purpose, and that is the baton rather
 * than a duplication: this panel answers WHO, in text, and the same seat's
 * roster card answers WHERE TO LOOK, in glow. Across a room the text is the
 * part you may not be able to read. See utils/boardFocus.js — and note the
 * grid used to mark the seat AFTER this one, which was reversed on purpose.
 *
 * Presentational: every input is a prop, no context, so /watch renders it
 * outside PlayerCardProvider.
 *
 * 🔴 THE CLOCK IS DISPLAY ONLY. It never commits a sale, never clears the
 * block, never advances the nominator. At zero it shows zero and stops.
 *
 * `showClips` OPTS IN to the highlight backdrop and DEFAULTS OFF, which is what
 * gates it to the operator's screen — /watch renders the same component and
 * simply does not pass it. See ClipBackdrop below for why.
 */
// How long SOLD holds the block. Long enough for a room to look up at it, short
// enough that the next nomination is never waiting behind it.
const SOLD_MS = 3500;

/**
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THE NOMINATION-HEIGHT RESERVE. The tallest this area ever gets — which is
 * the ON BLOCK card — so that at TV the space is claimed once and NOTHING ON
 * THE BOARD EVER MOVES.
 *
 * WHY IT HAD TO EXIST. `gridTop` swings ~130px every time a player goes on the
 * block and back on every sale: 168 times a draft. Nobody flagged it because
 * everything is small, and at 20px on a television it would be the most
 * noticeable thing in the room. It also breaks the type chooser outright — a
 * budget derived from a moving `gridTop` re-rungs twice per player and resizes
 * every roster card under the operator's hands at the exact moment the room is
 * looking at the board. The chooser's whole safety argument is that the budget
 * is a pure function of the viewport, and it is false without this.
 *
 * 🔴 DERIVED FROM THE CARD, NEVER A SECOND COPY OF IT. OnBlockState renders
 * from these same numbers below. A reserve measured once and typed in as 187
 * would be right until somebody changed the photo size, and then it would be
 * quietly wrong in the direction that clips a card on a screen nobody can
 * scroll.
 *
 * ⚠️ IT IS A MIN HEIGHT, NOT A FIXED ONE, AND THAT MAKES IT A FLOOR RATHER THAN
 * A FRAME. Anything taller than the reserve pushes the grid down again, exactly
 * the failure this exists to prevent — so "it fits" is not a hope, it is
 * asserted per state in tvMode.test.js off the model below, and the two things
 * that CAN appear beside the block (the restore banner, the countdown) are out
 * of flow at TV so they cannot contribute to the total at all. It stays a min
 * because a pathologically long name can wrap the text column past the photo:
 * growing is ugly, and clipping the player everyone is looking at is not
 * survivable.
 *
 * ⚠️ WIDTH-CONDITIONAL. The ON BLOCK name is `clamp(28px, 5.5vw, 58px)` and the
 * waiting name is `clamp(26px, 4.5vw, 48px)`, so both are at their MAXIMUM only
 * above roughly 1055px of viewport. This constant models the maxed-out card,
 * which is correct at every TV width and at every laptop width the board is
 * usable on — but it is NOT a universal height. Below ~1055px the type shrinks
 * with the viewport and the real card is shorter than the reserve, which is
 * harmless (the reserve is only applied at TV). If anyone reuses this below TV
 * or on a phone, that is the assumption they have to check first.
 * ══════════════════════════════════════════════════════════════════════
 */
export const NOM_BLOCK = {
  photo: 116,        // PlayerPhoto size in OnBlockState
  photoRing: 2,      // the position-coloured border, per side
  padY: 20,          // the panel's own vertical padding
  labelPx: 11,       // theme.fontSize.caption — "ON THE BLOCK"
  labelLead: 1.2,
  labelGap: 12,      // that label's marginBottom
  border: 1,         // the panel's own border, per side
};

export const NOMINATION_RESERVE = Math.round(
  NOM_BLOCK.border * 2
  + NOM_BLOCK.padY * 2
  + NOM_BLOCK.labelPx * NOM_BLOCK.labelLead + NOM_BLOCK.labelGap
  + NOM_BLOCK.photo + NOM_BLOCK.photoRing * 2
);

/**
 * Every vertical number in the other two states, so `nominationStateHeight`
 * below and the components themselves read the same values.
 *
 * 🔴 THE `*Px` VALUES ARE THE CLAMP MAXIMA, which is what applies at TV — see
 * the width note above. The components interpolate them into their clamp()
 * strings rather than repeating the digits, so raising a font size moves the
 * model in the same commit and the fit test goes red instead of the board
 * silently overflowing on draft night.
 */
export const NOM_TYPE = {
  waiting: {
    labelPx: 11, labelLead: 1.2, labelGap: 8,     // "Waiting on"
    namePx: 48, nameLead: 1.05,                   // "BEN'S NOMINATION"
    clockGap: 12, clockPx: 52, clockLead: 1,
  },
  sold: {
    headPx: 46, headLead: 1.05,                   // "SOLD!"
    nameGap: 6, namePx: 48, nameLead: 1.1,
    lineGap: 8, linePx: 22, lineLead: 1.2,        // "$47 to Rachel"
  },
  // COMPLETE borrows WAITING's geometry deliberately: same label, same big
  // line, no clock. So it is the no-clock waiting height by construction and
  // cannot outgrow the reserve while waiting fits — one fewer number to keep
  // in sync, and the fit test still measures it as its own state.
  complete: {
    labelPx: 11, labelLead: 1.2, labelGap: 8,     // "Final"
    namePx: 48, nameLead: 1.05,                   // "DRAFT COMPLETE"
  },
};

/**
 * The modelled height of one nomination state at TV, in px.
 *
 * ⚠️ A MODEL, BECAUSE jsdom DOES NO LAYOUT. getBoundingClientRect is all zeros
 * in a test, so the alternative to modelling is not measuring — it is not
 * checking. What this buys is the thing that actually breaks: a font bump that
 * pushes a state past the reserve becomes a red build rather than a grid that
 * moves at pick one on a television.
 *
 * The WAITING clock is optional (`nomClockSec: 0` is the default), so the
 * argument exists — but the fit test uses the WITH-clock height, because the
 * reserve has to hold the tallest reachable state, not the usual one.
 */
export function nominationStateHeight(mode, { clock = true } = {}) {
  const frame = NOM_BLOCK.border * 2 + NOM_BLOCK.padY * 2;
  if (mode === "block") return NOMINATION_RESERVE;
  if (mode === "sold") {
    const s = NOM_TYPE.sold;
    return Math.round(frame
      + s.headPx * s.headLead
      + s.nameGap + s.namePx * s.nameLead
      + s.lineGap + s.linePx * s.lineLead);
  }
  if (mode === "complete") {
    const c = NOM_TYPE.complete;
    return Math.round(frame
      + c.labelPx * c.labelLead + c.labelGap
      + c.namePx * c.nameLead);
  }
  const w = NOM_TYPE.waiting;
  return Math.round(frame
    + w.labelPx * w.labelLead + w.labelGap
    + w.namePx * w.nameLead
    + (clock ? w.clockGap + w.clockPx * w.clockLead : 0));
}

/**
 * 🔴 `focus` COMES FROM THE CONTAINER AND IS NOT RECOMPUTED HERE. Both this
 * component and RosterBoard render from one `boardFocus(board, useSoldFlash())`
 * answer, so the banner and the grid can never point at different things — see
 * the header of utils/boardFocus.js for the failure that prevents.
 *
 * ⚠️ THE DEFAULT IS "no sold flash was measured for me", NOT a second copy of
 * the precedence rule: it calls the same function with a null flash, which is
 * exactly right for a direct render in a test. Both real containers pass it,
 * and nominationBlock.test.js asserts they do — otherwise SOLD would vanish
 * from a surface and nothing would fail.
 */
export default function NominationBlock({
  board, focus, onCancel, readOnly = false, showClips = false, fill = false,
}) {
  const seconds = useNominationSeconds(board);
  const f = focus || boardFocus(board, null);

  /**
   * 🔴 `fill` MAKES EVERY STATE THE SAME HEIGHT, and it is what turns the
   * reserve from wasted space into the feature.
   *
   * ⚠️ THIS REPLACED `compactWaiting`, WHICH EXISTED TO SAVE EXACTLY THIS
   * SPACE. Its reasoning was "one line of text in a panel sized for all that,
   * which on a TV costs ~150px of board nobody can scroll to" — and the
   * objection there is to the AIR, not to the height. Inside a fixed reserve
   * the compact panel IS that air: the ~150px is claimed either way, and a
   * small line floating in the middle of it is the worst of both. So the full
   * waiting state comes back at TV, centred, answering "who are we waiting on"
   * at a size that reads from ten feet — the text half of the same message the
   * pulsing roster card is sending spatially.
   *
   * 🔴 ON BLOCK IS NOT SHRUNK TO MEET IT. That card earns every pixel and is
   * the element this whole feature set is built around; the reserve is sized
   * FROM it, so it is untouched by definition.
   */
  const fillStyle = fill
    ? { flex: "1 1 auto", display: "flex", flexDirection: "column", justifyContent: "center" }
    : null;

  if (f.mode === "block") {
    return <OnBlockState nomination={board.nomination} onCancel={onCancel} readOnly={readOnly} showClips={showClips} fillStyle={fillStyle} />;
  }
  if (f.mode === "sold") return <SoldState sale={f.sale} teamName={board?.teamNames?.[f.sale.team]} fillStyle={fillStyle} />;
  // 🔴 IT REPLACES WAITING, AND ONLY WAITING. Block and sold keep their
  // precedence: the final sale's SOLD flash gets its three and a half seconds
  // before this lands, which is the one moment the room most wants it. After
  // that there is no question left to ask — see CompleteState.
  if (boardIsComplete(board)) return <CompleteState fillStyle={fillStyle} />;
  return <WaitingState board={board} seconds={seconds} fillStyle={fillStyle} />;
}

/**
 * 🔴 THE WAITING PANEL PULSES, AND IT NEVER DID BEFORE.
 *
 * Reported as a regression after ce99008 — "we lost our nice glow pulsing for
 * when it's saying who's nomination it is". It was not lost: neither waiting
 * branch had ever carried an animation, and ce99008 touched only RosterBoard.
 * It is a thing that should exist and did not, and it belongs to the baton: the
 * lit element pulses, and in the waiting state the lit element is this panel
 * plus the nominator's card.
 *
 * SAME KEYFRAME AS ON THE BLOCK, AND FASTER THAN THE CARD'S. The banner is the
 * loud half of one message — it answers WHO — and the card is the quiet half
 * answering WHERE. Keeping the weighting is what makes the eye travel banner →
 * card instead of choosing between two equals.
 */
const WAITING_PULSE = "nomGlowPulse 1.9s ease-in-out infinite";

// ── WAITING ─────────────────────────────────────────────────────────
/**
 * 🔴 THERE IS ONE WAITING RENDERING, AND IT IS THE BIG ONE. A `compact` variant
 * shipped Aug 12 to save ~150px at TV and was removed the same day: the reserve
 * claims that space unconditionally now, so a small line inside it is the air
 * its own comment objected to rather than a saving. See `fill` above.
 */
function WaitingState({ board, seconds, fillStyle }) {
  // 🔴 THROUGH `turnSeat`, NOT OFF THE RAW INDEX. This was the one place on
  // either surface that read `board.nominatorIndex` directly — so the guard
  // (< 0 is nobody, junk is nobody) and the wrap applied to the roster glow and
  // the board menu but not to the banner naming the same person. Exactly the
  // "trivially equal until somebody touches one of them" case boardFocus.js's
  // own header warns about.
  const seat = turnSeat(board);
  const name = seat >= 0 ? board?.teamNames?.[seat] : null;
  /**
   * ══════════════════════════════════════════════════════════════════════
   * 🔴 THE SEAT THIS PANEL IS NAMING MAY HAVE NOTHING LEFT TO NOMINATE.
   *
   * Ben: "If I manually select a nominator that has a full team, we should get
   * a message that that team is complete and has no more nominations." The
   * rotation skips finished seats on write, so this is only reachable through
   * `setNominator` — which is the escape hatch and stays permissive. So the
   * board says what is true rather than refusing the choice.
   *
   * 🔴 IT COSTS NO LAYOUT, WHICH IS NOT OPTIONAL. `nominationStateHeight`
   * models every state so the grid cannot jump at TV, and waiting-with-clock
   * already runs within ~9px of NOMINATION_RESERVE — an extra text line does
   * not fit, and growing the reserve moves the grid down on the one screen
   * nobody can scroll. A ribbon absolutely positioned across the panel's top
   * edge sits inside the panel's own 20px padding, adds nothing to the column,
   * and cannot collide with the clock at the bottom. So the modelled height is
   * unchanged and tvMode.test.js keeps grading the same numbers.
   * ══════════════════════════════════════════════════════════════════════
   */
  const seatDone = seat >= 0 && !!board && teamIsFull(board, seat);
  const low = seconds != null && seconds <= 10;
  const clockColour = seconds === 0 ? theme.colors.status.danger
    : low ? "#fbbf24" : theme.colors.text.secondary;

  return (
    <div className="mock-nominating-panel" data-testid="nomination-block" style={{
      position: "relative", overflow: "hidden",
      textAlign: "center", padding: `${NOM_BLOCK.padY}px`,
      background: theme.colors.bg.surface,
      border: `${NOM_BLOCK.border}px solid ${theme.colors.border.brand}`,
      borderRadius: theme.radius.xl,
      animation: WAITING_PULSE,
      ...fillStyle,
    }}>
      {/* Out of flow — see the note on `seatDone`. It rides in the panel's own
          padding, so the modelled height of this state does not move. */}
      {seatDone && (
        <div
          data-testid="nomination-roster-full"
          style={{
            position: "absolute", top: 0, left: 0, right: 0,
            background: "rgba(234,179,8,0.16)",
            borderBottom: "1px solid rgba(234,179,8,0.35)",
            color: "#fde68a", fontSize: theme.fontSize.caption,
            fontWeight: theme.fontWeight.heavy, letterSpacing: "0.14em",
            textTransform: "uppercase", lineHeight: 1.3, padding: "2px 8px",
          }}
        >Roster full, no nominations left</div>
      )}
      {/* 🔴 EVERY VERTICAL NUMBER HERE COMES FROM NOM_TYPE.waiting, so
          nominationStateHeight models what this actually renders. Bump a size
          and the fit test moves with it. */}
      <div style={{
        fontSize: NOM_TYPE.waiting.labelPx, lineHeight: NOM_TYPE.waiting.labelLead,
        letterSpacing: "0.22em", textTransform: "uppercase",
        color: theme.colors.text.faint, fontWeight: theme.fontWeight.heavy,
        marginBottom: NOM_TYPE.waiting.labelGap,
      }}>Waiting on</div>

      <div style={{
        fontFamily: theme.fonts.display, fontWeight: theme.fontWeight.black,
        fontSize: `clamp(26px, 4.5vw, ${NOM_TYPE.waiting.namePx}px)`,
        lineHeight: NOM_TYPE.waiting.nameLead,
        color: theme.colors.brand.accent, letterSpacing: "-0.5px", wordBreak: "break-word",
      }}>{name ? `${name.toUpperCase()}'S NOMINATION` : "A NOMINATION"}</div>

      {seconds != null && (
        <div style={{
          marginTop: NOM_TYPE.waiting.clockGap,
          fontVariantNumeric: "tabular-nums", fontFamily: theme.fonts.display,
          fontSize: `clamp(30px, 5vw, ${NOM_TYPE.waiting.clockPx}px)`,
          fontWeight: theme.fontWeight.black, lineHeight: NOM_TYPE.waiting.clockLead,
          color: clockColour,
        }}>{Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}</div>
      )}
    </div>
  );
}

// ── COMPLETE ────────────────────────────────────────────────────────
/**
 * 🔴 A DRAFT THAT HAS ENDED MUST NOT KEEP ASKING A QUESTION. With every roster
 * full there is no next nominator, so "WAITING ON JENNY'S NOMINATION" is a
 * prediction that is not merely wrong but unanswerable — and it is the last
 * thing eleven people are left looking at.
 *
 * A FACT, so it belongs on /watch too and is gated on nothing but the board.
 *
 * GREEN, AND IT DOES NOT PULSE — the same rule SOLD follows in this file. The
 * pulse is the WAITING verb, an unanswered question. This is a result.
 */
function CompleteState({ fillStyle }) {
  return (
    <div className="mock-nominating-panel" data-testid="nomination-block" style={{
      textAlign: "center", padding: `${NOM_BLOCK.padY}px`,
      background: "rgba(34,197,94,0.10)",
      border: `${NOM_BLOCK.border}px solid rgba(34,197,94,0.45)`,
      borderRadius: theme.radius.xl,
      ...fillStyle,
    }}>
      {/* Same pact as the other two states: every vertical number is
          NOM_TYPE.complete, so nominationStateHeight models what this renders
          and a font bump goes red in tvMode.test.js instead of overflowing the
          reserve on a television. */}
      <div style={{
        fontSize: NOM_TYPE.complete.labelPx, lineHeight: NOM_TYPE.complete.labelLead,
        letterSpacing: "0.22em", textTransform: "uppercase",
        color: theme.colors.text.faint, fontWeight: theme.fontWeight.heavy,
        marginBottom: NOM_TYPE.complete.labelGap,
      }}>Final</div>

      <div style={{
        fontFamily: theme.fonts.display, fontWeight: theme.fontWeight.black,
        fontSize: `clamp(26px, 4.5vw, ${NOM_TYPE.complete.namePx}px)`,
        lineHeight: NOM_TYPE.complete.nameLead,
        color: "#4ade80", letterSpacing: "-0.5px",
      }}>DRAFT COMPLETE</div>
    </div>
  );
}

// ── SOLD ────────────────────────────────────────────────────────────
/**
 * The loud version of a moment the board used to pass over in silence.
 *
 * Green rather than the block's orange, and no photo: this is a result, not the
 * thing being bid on, and it holds the screen for a few seconds. Three facts,
 * in the order the room shouts them — that it sold, who it was, what it went
 * for and to whom.
 */
function SoldState({ sale, teamName, fillStyle }) {
  return (
    <div className="mock-nominating-panel" data-testid="nomination-block" style={{
      textAlign: "center", padding: `${NOM_BLOCK.padY}px`,
      background: "rgba(34,197,94,0.10)",
      border: `${NOM_BLOCK.border}px solid rgba(34,197,94,0.45)`,
      borderRadius: theme.radius.xl,
      ...fillStyle,
    }}>
      {/* Same pact as WAITING: these are NOM_TYPE.sold, and the model reads
          them. SOLD is the state with the least headroom in the reserve. */}
      <div style={{
        fontFamily: theme.fonts.display, fontWeight: theme.fontWeight.black,
        fontSize: `clamp(26px, 4.5vw, ${NOM_TYPE.sold.headPx}px)`,
        lineHeight: NOM_TYPE.sold.headLead,
        color: "#4ade80", letterSpacing: "0.04em",
      }}>SOLD!</div>

      <div style={{
        fontFamily: theme.fonts.display, fontWeight: theme.fontWeight.black,
        fontSize: `clamp(24px, 4.5vw, ${NOM_TYPE.sold.namePx}px)`,
        lineHeight: NOM_TYPE.sold.nameLead, marginTop: NOM_TYPE.sold.nameGap,
        color: "#fff", letterSpacing: "-0.5px", wordBreak: "break-word",
      }}>{sale.name}</div>

      <div style={{
        marginTop: NOM_TYPE.sold.lineGap,
        fontSize: `clamp(15px, 2vw, ${NOM_TYPE.sold.linePx}px)`,
        lineHeight: NOM_TYPE.sold.lineLead,
        color: theme.colors.text.secondary, fontWeight: theme.fontWeight.bold,
      }}>
        {/* Each its own element: the price paid and the winning seat are two
            separate facts and are read as two by anything querying this. */}
        <span style={{ color: "#4ade80", fontWeight: theme.fontWeight.black }}>${sale.price}</span>
        {" to "}
        <span style={{ color: theme.colors.text.primary }}>{teamName || "—"}</span>
      </div>
    </div>
  );
}

// ── ON THE BLOCK ────────────────────────────────────────────────────
function OnBlockState({ nomination, onCancel, readOnly, showClips, fillStyle }) {
  // 🔴 NULL FOR ALMOST EVERYBODY, AND null IS THE WHOLE FALLBACK. No entry in
  // player-clips.json, clips killed for this device, or the spectator view not
  // opting in — all three land here, and everything below renders as it always
  // has. `lift` is spread, so with no clip it contributes literally nothing to
  // the style objects and the markup is byte-identical to the shipped card.
  const clip = showClips ? clipFor(nomination.name) : null;
  const lift = clip ? { position: "relative", zIndex: 1 } : null;

  return (
    <div
      className="mock-nominating-panel"
      data-testid="nomination-block"
      style={{
        // 🔴 THESE ARE THE NUMBERS NOMINATION_RESERVE IS DERIVED FROM. Change
        // one and the reserve follows, because it is computed from them rather
        // than measured off a screenshot once.
        position: "relative", textAlign: "center", padding: `${NOM_BLOCK.padY}px`,
        background: theme.colors.bg.deep,
        border: `${NOM_BLOCK.border}px solid ${theme.colors.border.brand}`,
        borderRadius: theme.radius.xl,
        animation: "nomGlowPulse 1.9s ease-in-out infinite",
        ...fillStyle,
      }}
    >
      {clip && <ClipBackdrop url={clip.url} focus={clip.focus} />}

      <div style={{
        fontSize: theme.fontSize.caption, letterSpacing: "0.22em", textTransform: "uppercase",
        color: theme.colors.brand.accent, fontWeight: theme.fontWeight.heavy,
        marginBottom: NOM_BLOCK.labelGap, lineHeight: NOM_BLOCK.labelLead,
        ...lift,
      }}>On the block</div>

      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: 20, flexWrap: "wrap",
        ...lift,
      }}>
        <PlayerPhoto player={nomination} size={NOM_BLOCK.photo} />
        <div style={{ textAlign: "left", minWidth: 0 }}>
          <div style={{
            fontFamily: theme.fonts.display, fontWeight: theme.fontWeight.black,
            fontSize: "clamp(28px, 5.5vw, 58px)", lineHeight: 1.05, color: "#fff",
            letterSpacing: "-0.5px", wordBreak: "break-word",
          }}>{nomination.name}</div>
          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{
              ...theme.helpers.posStyle(nomination.pos),
              color: POS_COLORS[nomination.pos]?.text || theme.colors.text.primary,
              fontSize: theme.fontSize.sub, fontWeight: theme.fontWeight.heavy,
              padding: "3px 12px", borderRadius: 6, letterSpacing: "0.06em",
            }}>{nomination.pos}</span>
            {nomination.team && (
              <span style={{
                fontSize: theme.fontSize.heading, color: theme.colors.text.muted,
                fontWeight: theme.fontWeight.bold, letterSpacing: "0.06em",
              }}>{nomination.team}</span>
            )}
          </div>
        </div>
      </div>

      {!readOnly && onCancel && (
        <button
          onClick={onCancel}
          aria-label="Clear the block"
          style={{
            position: "absolute", top: 10, right: 12,
            background: "transparent", border: `1px solid ${theme.colors.border.default}`,
            borderRadius: theme.radius.sm, color: theme.colors.text.muted,
            fontFamily: "inherit", fontSize: theme.fontSize.micro,
            padding: "3px 9px", cursor: "pointer",
          }}
        >passed</button>
      )}
    </div>
  );
}

/**
 * ClipBackdrop — a muted highlight loop behind the player on the block.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 DECORATION THAT MUST NEVER COST ANYTHING WHEN IT FAILS. Three separate
 * ways out, in the order they fire:
 *   1. No entry in player-clips.json — this component is never mounted, and
 *      that is the case for ~300 of ~302 players.
 *   2. The CLIPS toggle off for this device — same, never mounted. One click,
 *      no deploy (ClipToggle.jsx).
 *   3. The video errors — a 404 on a stale id, Giphy down, no network at the
 *      venue — and `onError` unmounts it. The card underneath is the shipped
 *      card, already painted.
 * There is no loading state and no placeholder: an element that has not loaded
 * is transparent, so the room sees today's card until the moment it is not.
 *
 * 🔴 NO AUDIO, EVER. `muted` is set as a prop AND forced on the element by ref,
 * because React has historically not reflected `muted` as an attribute and this
 * is a room with eleven people in it. `playsInline` keeps iOS from taking the
 * video fullscreen, which on a phone would replace the board with a GIF.
 *
 * 🔴 NOT ON /watch — see the gate in NominationBlock. `preload="none"` is here
 * anyway: autoplay still fetches, but nothing is pulled before the element is
 * on screen, and the browser is told this is not worth queueing ahead of the
 * board itself.
 *
 * `focus` is an optional `object-position` off the data file — the clip is
 * cropped to `cover`, and which part survives that crop is a per-clip judgement
 * nobody can make in the abstract (Henry's is framed too low). Absent, and no
 * property is emitted at all: see the note on the style object below.
 *
 * The scrim is what makes the name readable over moving footage, and it is
 * LEFT-WEIGHTED (0.96 -> 0.25) per the mockup. ⚠️ The content row is still
 * centred, exactly as it shipped, so the fade reaches the name rather than
 * stopping short of it. That is deliberate — this went up to be judged on a
 * cast TV, not in a mockup, and `justifyContent: "flex-start"` on the row above
 * is the one-line change if it reads badly in the room.
 * ══════════════════════════════════════════════════════════════════════
 */
/**
 * The scrim, as a named constant because it is the LEGIBILITY MECHANISM and not
 * a decoration of a decoration — 0.96 opaque under the left edge, 0.25 at the
 * right, per the mockup. Exported so a test can pin those two numbers: jsdom
 * drops `linear-gradient` from inline styles entirely, so asserting it off the
 * rendered element would silently assert nothing.
 */
export const CLIP_SCRIM = "linear-gradient(90deg, rgba(10,15,26,0.96) 0%, rgba(10,15,26,0.25) 100%)";

/**
 * Exported so /rate's team report can reuse the CARD treatment exactly rather
 * than restating its dim and scrim as fresh literals. Nothing about the live
 * board changes; this is the same component with a keyword in front of it.
 *
 * `onError` is OPTIONAL and additive: the backdrop still hides itself on a dead
 * id exactly as it always did, and a caller that passes nothing behaves
 * byte-for-byte as before. It exists because /rate cycles through a roster's
 * clips and a dead id there has to skip to the next player rather than leave
 * the card blank until the dwell timer comes round again.
 */
export function ClipBackdrop({ url, focus, onError }) {
  const [failed, setFailed] = useState(false);

  // Reset per clip, or one bad id would suppress every later backdrop — the
  // same trap PlayerPhoto documents below.
  useEffect(() => { setFailed(false); }, [url]);

  if (failed) return null;

  return (
    <div
      data-testid="clip-backdrop"
      aria-hidden="true"
      style={{
        position: "absolute", inset: 0, overflow: "hidden",
        borderRadius: theme.radius.xl, pointerEvents: "none",
      }}
    >
      <video
        key={url}
        data-testid="clip-video"
        src={url}
        ref={(el) => { if (el) el.muted = true; }}
        autoPlay muted loop playsInline preload="none"
        onError={() => {
          setFailed(true);
          if (onError) onError(url);
        }}
        style={{
          width: "100%", height: "100%", objectFit: "cover", opacity: 0.42, display: "block",
          // 🔴 SPREAD, NOT A DEFAULT. `object-position` already defaults to
          // center, so an entry with no `focus` must contribute NOTHING here —
          // writing "center" out would be the same picture and a different
          // style attribute, and the regression test is on the attribute.
          ...(focus ? { objectPosition: focus } : null),
        }}
      />
      <div data-testid="clip-scrim" style={{ position: "absolute", inset: 0, background: CLIP_SCRIM }} />
    </div>
  );
}

/**
 * The photo, at size.
 *
 * The resolution rules are MiniAvatar's (ResultsScreen.jsx:198), reused rather
 * than reinvented: a sleepercdn headshot by sleeper_id, initials on the
 * position colour when there is no id or the image fails, and DEF never gets a
 * headshot because there is no person to photograph.
 *
 * 🔴 THE onError FALLBACK IS THE POINT AT THIS SIZE. A broken-image icon 116px
 * across, on a screen eleven people are watching, is very visible — the swap to
 * initials happens on the error, so nothing broken ever paints twice.
 *
 * DEF gets more than the bare abbreviation MiniAvatar shows, because "HOU"
 * alone in a 116px circle reads as a placeholder rather than as a defense. It
 * tries the team logo and falls back to the abbreviation set large.
 * ⚠️ The logo path is the same host as the headshots but is NOT otherwise used
 * in this app — if it 404s the fallback below is what shows, which is exactly
 * today's behaviour, so the downside is bounded.
 */
export function PlayerPhoto({ player, size = 116 }) {
  const [imgFailed, setImgFailed] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const pos = player?.pos || "BN";
  const sid = player?.sleeper_id;
  const team = player?.team;

  // Reset on a new player, or one failure would suppress every later photo.
  useEffect(() => { setImgFailed(false); setLogoFailed(false); }, [player?.name]);

  const ring = {
    width: size, height: size, borderRadius: "50%", flexShrink: 0,
    objectFit: "cover", background: "#0d1320",
    border: `${NOM_BLOCK.photoRing}px solid ${POS_COLORS[pos]?.text || theme.colors.border.default}`,
  };

  // DEF is a team, not a person.
  if (pos === "DEF") {
    if (team && !logoFailed) {
      return (
        <img
          src={`https://sleepercdn.com/images/team_logos/nfl/${String(team).toLowerCase()}.png`}
          alt="" onError={() => setLogoFailed(true)}
          style={{ ...ring, objectFit: "contain", padding: 10, background: theme.colors.bg.surface }}
        />
      );
    }
    return (
      <div data-testid="photo-fallback" style={{
        ...ring, display: "flex", alignItems: "center", justifyContent: "center",
        background: POS_COLORS.DEF?.bg || "#64748b",
        fontFamily: theme.fonts.display, fontWeight: theme.fontWeight.black,
        fontSize: Math.round(size * 0.3), color: "#fff",
      }}>{team || "DEF"}</div>
    );
  }

  if (sid && !imgFailed) {
    return (
      <img
        src={`https://sleepercdn.com/content/nfl/players/${sid}.jpg`}
        alt="" onError={() => setImgFailed(true)} style={ring}
      />
    );
  }

  const parts = (player?.name || "").split(" ");
  const initials = parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : (parts[0]?.[0] || "?").toUpperCase();
  return (
    <div data-testid="photo-fallback" style={{
      ...ring, display: "flex", alignItems: "center", justifyContent: "center",
      background: POS_COLORS[pos]?.bg || "#64748b",
      fontFamily: theme.fonts.display, fontWeight: theme.fontWeight.black,
      fontSize: Math.round(size * 0.36), color: "#fff",
    }}>{initials}</div>
  );
}

/**
 * Seconds left, recomputed on a ticker.
 *
 * The VALUE comes from persisted board state (utils/liveBoard.js), not from
 * when this component mounted — that is what makes the operator's clock and
 * every spectator's clock the same number. The interval here only forces a
 * re-render; it decides nothing and it stops the moment somebody is up.
 *
 * It lives in a display component so useLiveBoard stays timer-free, which the
 * reliability bar requires and a test enforces.
 */
/**
 * The last sale, for as long as it is NEWS.
 *
 * 🔴 THE TRIGGER IS A CHANGE, NOT AN AGE. "The last sale is less than N seconds
 * old" is the obvious version and it is wrong on the surface that matters:
 * /watch polls every 4s, so a 2-second window is missed more often than it is
 * hit — the flash would appear on the operator's screen and almost never on the
 * room's. Firing on the change instead makes the poll interval irrelevant, and
 * the N seconds run from the moment each screen SAW it.
 *
 * 🔴 FIRST SIGHT SEEDS AND NEVER FIRES. Otherwise a spectator opening the link
 * mid-draft is greeted by a SOLD for a pick from ten minutes ago.
 *
 * 🔴 ONLY AN APPEND COUNTS. An undo, a delete, or an edit in place clears the
 * flash instead of firing it. Undo is the sharp case twice over: eleven people
 * must not be left looking at a sale that no longer exists, and a naive
 * last-sale-changed check would then flash the sale BEFORE it — a pick from
 * minutes ago, presented as if it just happened.
 *
 * Identity is a STRING of the sale's fields, not the object reference: /watch
 * rebuilds the whole board every poll, so a reference check fires every 4s
 * forever. For the same reason the effect depends on that string alone — depend
 * on the sale object and each poll re-runs the effect, whose cleanup cancels the
 * pending expiry timer and leaves SOLD on screen permanently.
 *
 * Lives here, with the other timer, so useLiveBoard stays timer-free — the
 * reliability bar requires it and a test enforces it.
 */
export function useSoldFlash(board, ms = SOLD_MS) {
  const sales = board?.sales;
  const count = sales?.length || 0;
  const last = count ? sales[count - 1] : null;
  const key = last ? `${count}|${last.name}|${last.price}|${last.team}|${last.ts}` : `${count}|`;
  /**
   * 🔴 NO BOARD IS NOT AN OBSERVATION OF ZERO SALES, and conflating them breaks
   * the two rules above in opposite directions.
   *
   * This hook now runs in the CONTAINER, which mounts before there is a board:
   * /watch is "loading", and /live is on the setup screen. Treating that as a
   * seen state of `count: 0` makes the first poll's six-sale board look like six
   * sales appearing at once — the stale SOLD a spectator opening mid-draft must
   * never get. Resetting to unseen instead makes the first REAL board first
   * sight, which is what it is.
   *
   * `has` is therefore in the deps: null -> a fresh board with no sales does not
   * change `key` ("0|" either way), so without it the seed never happens and the
   * FIRST sale of the auction is the one pick that never flashes.
   *
   * ⚠️ It is a boolean that flips once, not a per-poll value — the trap the
   * comment above names (an unstable dep re-running the effect and cancelling
   * the pending expiry timer) needs a dep that changes while SOLD is up. This
   * one cannot.
   */
  const has = !!board;

  const [flash, setFlash] = useState(null);
  const nowRef = useRef(null);
  nowRef.current = { key, count, last, has };
  const seenRef = useRef(null);

  useEffect(() => {
    const cur = nowRef.current;
    const prev = seenRef.current;
    if (!cur.has) {                                      // no board: unseen, not zero
      seenRef.current = null;
      return undefined;
    }
    seenRef.current = { key: cur.key, count: cur.count };

    if (prev === null) return undefined;                 // first sight: seed only
    if (cur.key === prev.key) return undefined;          // same sale, new object
    if (cur.count <= prev.count) {                       // undo, delete, or edit
      setFlash(null);
      return undefined;
    }
    setFlash(cur.last);
    const t = setTimeout(() => setFlash(null), ms);
    return () => clearTimeout(t);
  }, [key, has, ms]);

  return flash;
}

export function useNominationSeconds(board) {
  const [now, setNow] = useState(() => Date.now());
  // Matches nominationSecondsLeft's own gates, so the pre-draft board that
  // shows no clock also runs no interval behind it.
  const running = !!board?.nomClockSec && !board?.nomination
    && (board?.sales || []).length > 0;

  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [running]);

  return nominationSecondsLeft(board, running ? now : Date.now());
}
